/**
 * The page research read, as it saw it — the capture kept with the evidence,
 * opened by a short-lived link in a sandboxed frame (no scripts, no forms, no
 * navigation out), with the page's own address and when it was read.
 */

import { useEffect, useState } from 'react';

import { Anchor, Center, Loader, Modal, Stack, Text } from '@mantine/core';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { CaptureLink } from '@/types/workbench';
import { toError } from '@/utils/errors';

interface CaptureViewerProps {
  reportId: string;
  evidenceId: string;
  opened: boolean;
  onClose: () => void;
}

export function CaptureViewer({
  reportId,
  evidenceId,
  opened,
  onClose,
}: Readonly<CaptureViewerProps>) {
  const [link, setLink] = useState<CaptureLink | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    let cancelled = false;
    setLink(null);
    setFailed(null);
    vnextApiClient
      .getEvidenceCapture(reportId, evidenceId)
      .then((found) => !cancelled && setLink(found))
      .catch((err) => {
        Sentry.captureException(toError(err));
        if (!cancelled) {
          setFailed(toError(err, "The capture couldn't be opened").message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [opened, reportId, evidenceId]);

  return (
    <Modal opened={opened} onClose={onClose} size="90%" title="As research saw it">
      <Stack gap="xs" data-testid="capture-viewer">
        {link?.sourceUrl ? (
          <Text size="xs" c="dimmed">
            <Anchor href={link.sourceUrl} target="_blank" rel="noopener noreferrer" size="xs">
              {link.sourceUrl}
            </Anchor>
            {link.retrievedAt
              ? ` — read ${new Date(link.retrievedAt).toLocaleString('en-GB', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}`
              : ''}
          </Text>
        ) : null}
        {failed ? (
          <Text size="sm" c="red">
            {failed}
          </Text>
        ) : null}
        {!link && !failed ? (
          <Center h={200}>
            <Loader size="sm" />
          </Center>
        ) : null}
        {link ? (
          <iframe
            title="The page as captured"
            src={link.url}
            sandbox=""
            referrerPolicy="no-referrer"
            style={{
              width: '100%',
              height: '70vh',
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
            data-testid="capture-frame"
          />
        ) : null}
      </Stack>
    </Modal>
  );
}
